<template>
  <div class="container--devices-analytics-v3">
    <main class="main--devices-analytics-v3">
      <div class="card--devices-analytics-v3" v-if="!noInfoFound">
        <div class="d-flex justify-space-between">
          <span class="body-bold-medium">{{
            $t('message.pages.analyticsV3.pages.devices.title')
          }}</span>
          <span class="body-regular-smaller neutral-40-light--text">{{
            $t('message.pages.analyticsV3.pages.devices.updatedAgo', {
              time: lastUpdatedMinutes,
            })
          }}</span>
        </div>

        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.analyticsV3.pages.devices.description') }}
          <a
            class="body-bold-smaller neutral-30-base--text span-link"
            @click="openHelpArticle"
          >
            {{ $t('message.commom.learnMore') }}
            <v-icon size="12" color="neutral-30-base">mdi-open-in-new</v-icon>
          </a>
        </span>
        <v-row class="charts-container" v-if="loading">
          <v-col>
            <DonutSkeleton />
          </v-col>
          <v-col>
            <CardItemSkeleton />
          </v-col>
          <v-col>
            <CardItemSkeleton />
          </v-col>
          <v-col>
            <CardItemSkeleton />
          </v-col>
        </v-row>
        <div v-else ref="chartsContainer">
          <HeadCards
            :series="series"
            :summarizedDevicesInfo="summarizedDevicesInfo"
          />
        </div>
      </div>
      <div v-if="isCapturingPng" class="capture-clone">
        <div ref="chartsContainerForCapture" class="card--devices-analytics-v3">
          <div class="d-flex justify-space-between">
            <span class="body-bold-medium neutral-20-dark--text">
              {{ $t('message.pages.analyticsV3.pages.devices.title') }}
            </span>
          </div>
          <span class="body-regular-smaller neutral-40-light--text">
            {{ $t('message.pages.analyticsV3.pages.devices.description') }}
          </span>

          <div class="mt-6"> <HeadCards
              :series="series"
              :summarizedDevicesInfo="summarizedDevicesInfo"
            />
          </div>
        </div>
      </div>
      <div v-if="isCapturingPdf" class="capture-pdf-clone">
          <div ref="chartsContainerForPdf" class="card--devices-analytics-v3">
            <div class="mt-6"> <HeadCards
              :series="series"
              :summarizedDevicesInfo="summarizedDevicesInfo"
            />
          </div>
        </div>
      </div>

      <div class="card--table-devices-analytics-v3">
        <BaseTable
          :columns="visibleTableColumns"
          :items="tableItems"
          :showPagination="false"
          :showActionColumn="true"
          :showSummary="false"
          :loading="loading"
          :showTotalLabel="false"
          @update-visible-columns="currentlyVisibleColumns = $event"
          noPointer
        >
          <template #playCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.play).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #unique_playCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.unique_play).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #viewCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.view).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #unique_viewCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.unique_view).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #play_rateCell="{ row }">
            <span class="body-regular-small">{{ row.play_rate }}</span>
          </template>
          <template #actionHeader />
          <template #actionCell>
            <span style="display: inline-block; width: 24px"></span>
          </template>
        </BaseTable>
      </div>
    </main>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { format } from 'date-fns'
import i18n from '@/translations/lang'
import CardItemSkeleton from '@/views/AnalyticsV3/components/SkeletonLoaders/CardItem.vue'
import DonutSkeleton from '@/views/AnalyticsV3/components/SkeletonLoaders/Donut.vue'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import HeadCards from './layout/HeadCards.vue'
import { exportToCsv } from '@/utils/csvExporter.js'
import { toPng } from 'html-to-image'
import { generatePdfBlob } from '@/utils/pdfExporter.js'
import currencyMixin from '../../mixins/currencyMixin'
import { getEmbeddedFontCss } from '@/utils/fontBase64.js'

export default {
  name: 'Devices',
  components: {
    CardItemSkeleton,
    DonutSkeleton,
    BaseTable,
    HeadCards,
  },
  mixins: [currencyMixin],
  computed: {
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
      selectedVideos: (state) =>
        state.analytics.filters.filterVideos.selectedVideos,
    }),
    currentLanguage() {
      return this.$i18next.language
    },
    helpArticle() {
      const urls = {
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      }
      return urls[this.$i18next.resolvedLanguage] || urls.pt
    },
    tableItems() {
      if (!Object.keys(this.devicesInfo).length) return []
      return Object.values(this.devicesInfo).map((device) => {
        const playRate =
          device.view > 0 ? Math.round((device.play / device.view) * 100) : 0
        return {
          device: device.name,
          play: device.play,
          unique_play: device.unique_play,
          view: device.view,
          unique_view: device.unique_view,
          play_rate: `${playRate}%`,
        }
      })
    },
    baseTableColumnsThatCanBeHidden() {
      return this.baseTableColumns.filter((col) => col.value !== 'device')
    },
    baseTableColumns() {
      return [
        {
          text: this.$t(
            'message.pages.analyticsV3.pages.devices.columns.devices'
          ),
          value: 'device',
          align: 'start',
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.pages.devices.columns.plays'
          ),
          value: 'play',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.pages.devices.tooltips.plays'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.pages.devices.columns.uniquePlays'
          ),
          value: 'unique_play',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.pages.devices.tooltips.uniquePlays'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.pages.devices.columns.impressions'
          ),
          value: 'view',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.pages.devices.tooltips.impressions'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.pages.devices.columns.uniqueImpressions'
          ),
          value: 'unique_view',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.pages.devices.tooltips.uniqueImpressions'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.pages.devices.columns.playRate'
          ),
          value: 'play_rate',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.pages.devices.tooltips.playRate'
          ),
        },
      ]
    },
    visibleTableColumns() {
      return this.baseTableColumns.map((col) => ({
        ...col,
        width: 100 / this.baseTableColumns.length + '%',
      }))
    },
    lastUpdatedMinutes() {
      return Math.floor(this.lastUpdatedAt / 60) || 1
    },
  },
  data() {
    return {
      isCapturingPng: false, 
      isCapturingPdf: false,
      loading: true,
      errors: {
        fetchError: false,
        shownErrMsg: '',
      },
      currentlyVisibleColumns: [],
      lastUpdatedAt: 0,
      lastUpdatedTimeout: null,
      devicesInfo: {},
      summarizedDevicesInfo: {
        desktop: {
          name: this.$i18next.t(
            'message.pages.analyticsV3.pages.devices.categories.desktop.name'
          ),
          color: '#3F55EE',
          icon: 'mdi-monitor',
          play: 0,
          unique_play: 0,
          unique_view: 0,
          view: 0,
          percentage: 0,
          tooltip: this.$i18next.t(
            'message.pages.analyticsV3.pages.devices.categories.desktop.tooltip'
          ),
        },
        mobile: {
          name: this.$i18next.t(
            'message.pages.analyticsV3.pages.devices.categories.mobile.name'
          ),
          color: '#20C1A4',
          icon: 'panda-phone',
          play: 0,
          unique_play: 0,
          unique_view: 0,
          view: 0,
          percentage: 0,
          tooltip: this.$i18next.t(
            'message.pages.analyticsV3.pages.devices.categories.mobile.tooltip'
          ),
        },
        others: {
          name: this.$i18next.t(
            'message.pages.analyticsV3.pages.devices.categories.others.name'
          ),
          color: '#FF882B',
          icon: 'mdi-screen-rotation',
          play: 0,
          unique_play: 0,
          unique_view: 0,
          view: 0,
          percentage: 0,
          tooltip: this.$i18next.t(
            'message.pages.analyticsV3.pages.devices.categories.others.tooltip'
          ),
        },
      },
      series: [],
      noInfoFound: false,
    }
  },
  mounted() {
    this.fetchData()
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$on('trigger-pdf-export', this.generatePdf)
  },
  beforeDestroy() {
    this.clearTimeIntervals()
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$off('trigger-pdf-export', this.generatePdf)
  },
  watch: {
    dateRange: {
      handler() {
        this.fetchData()
      },
    },
    selectedVideos: {
      handler() {
        this.fetchData()
      },
    },
    '$i18next.resolvedLanguage': {
      handler() {
        this.updateTranslations()
      },
    },
  },
  methods: {
    ...mapMutations('analytics', ['UPDATE_ERROR_STATE']),
    /**
     * Updates frozen translations due to summarizedDevicesInfo being a data property.
     */
    updateTranslations() {
      this.summarizedDevicesInfo.desktop.name = this.$t(
        'message.pages.analyticsV3.pages.devices.categories.desktop.name'
      )
      this.summarizedDevicesInfo.desktop.tooltip = this.$t(
        'message.pages.analyticsV3.pages.devices.categories.desktop.tooltip'
      )

      this.summarizedDevicesInfo.mobile.name = this.$t(
        'message.pages.analyticsV3.pages.devices.categories.mobile.name'
      )
      this.summarizedDevicesInfo.mobile.tooltip = this.$t(
        'message.pages.analyticsV3.pages.devices.categories.mobile.tooltip'
      )

      this.summarizedDevicesInfo.others.name = this.$t(
        'message.pages.analyticsV3.pages.devices.categories.others.name'
      )
      this.summarizedDevicesInfo.others.tooltip = this.$t(
        'message.pages.analyticsV3.pages.devices.categories.others.tooltip'
      )
    },
    openHelpArticle() {
      window.open(this.helpArticle, '_blank')
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
    resetData() {
      this.devicesInfo = {}
      Object.keys(this.summarizedDevicesInfo).forEach((key) => {
        this.summarizedDevicesInfo[key].play = 0
        this.summarizedDevicesInfo[key].unique_play = 0
        this.summarizedDevicesInfo[key].unique_view = 0
        this.summarizedDevicesInfo[key].view = 0
        this.summarizedDevicesInfo[key].percentage = 0
      })
      this.series = []
      this.noInfoFound = false
    },
    handleFetchError(err) {
      console.error(err)
      this.errors.fetchError = true
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
      this.UPDATE_ERROR_STATE(true)
    },
    clearTimeIntervals() {
      clearInterval(this.lastUpdatedTimeout)
      this.lastUpdatedTimeout = null
      this.lastUpdatedAt = 0
    },
    async fetchData() {
      this.clearErrors()
      this.clearTimeIntervals()
      this.noInfoFound = false
      this.loading = true
      try {
        if (this.selectedVideos?.length) {
          return await this.getSelectedVideosDeviceInfo()
        }
        const { data } = await newAnalyticsApi.getDevicesMetrics(
          format(this.dateRange.start, 'yyyy-MM-dd'),
          format(this.dateRange.end, 'yyyy-MM-dd')
        )
        this.lastUpdatedTimeout = setInterval(() => {
          this.lastUpdatedAt += 1
        }, 1000)
        if (!Object.keys(data).length) {
          return this.handleEmptyData()
        }
        this.setDevicesData(data)
      } catch (e) {
        this.handleFetchError(e)
      } finally {
        this.loading = false
      }
    },
    handleEmptyData() {
      this.resetData()
      this.noInfoFound = true
    },
    async getSelectedVideosDeviceInfo() {
      const devicesResponses = []
      const devicesData = {}

      for (let video of this.selectedVideos) {
        const { data } = await newAnalyticsApi.getSingleDeviceMetrics(
          video.videoId,
          format(this.dateRange.start, 'yyyy-MM-dd'),
          format(this.dateRange.end, 'yyyy-MM-dd')
        )
        devicesResponses.push(data)
      }

      const isEmptyData = devicesResponses.every(
        (device) => !Object.keys(device).length
      )

      if (isEmptyData) {
        return this.handleEmptyData()
      }

      devicesResponses.forEach((device) => {
        const eachDeviceKeys = Object.keys(device)
        if (eachDeviceKeys.length) {
          eachDeviceKeys.forEach((key) => {
            const deviceKey = devicesData[`${key}`]
            if (!deviceKey) devicesData[`${key}`] = {}
            const browserInfo = device[key]
            const eachBrowserDateArray = Object.keys(browserInfo)
            eachBrowserDateArray.forEach((day) => {
              let checkIfObjectHasDay = devicesData[key][day]
              if (!checkIfObjectHasDay)
                devicesData[key][day] = {
                  play: 0,
                  view: 0,
                  unique_play: 0,
                  unique_view: 0,
                }
              devicesData[key][day]['play'] += device[key][day]['play'] ?? 0
              devicesData[key][day]['unique_play'] +=
                device[key][day]['unique_play'] ?? 0
              devicesData[key][day]['unique_view'] +=
                device[key][day]['unique_view'] ?? 0
              devicesData[key][day]['view'] += device[key][day]['view'] ?? 0
            })
          })
        }
      })
      this.setDevicesData(devicesData)
    },
    setDevicesData(data) {
      this.resetData()

      this.handleEachDeviceData(data)
      this.handleAddDevicesPlayPercentage()

      this.updateChartSeries()
    },
    handleAddDevicesPlayPercentage() {
      const totalPlays =
        this.summarizedDevicesInfo.desktop.play +
        this.summarizedDevicesInfo.mobile.play +
        this.summarizedDevicesInfo.others.play

      Object.keys(this.summarizedDevicesInfo).forEach((key) => {
        this.summarizedDevicesInfo[key].percentage =
          totalPlays > 0
            ? Math.round(
                (this.summarizedDevicesInfo[key].play / totalPlays) * 100
              )
            : 0
      })
    },
    handleEachDeviceData(data) {
      Object.keys(data).forEach((device) => {
        let category = 'others'

        if (['Windows', 'Mac OS', 'Linux'].includes(device)) {
          category = 'desktop'
        } else if (['iOS', 'Android'].includes(device)) {
          category = 'mobile'
        }

        this.devicesInfo[device] = {
          name: device,
          view: 0,
          play: 0,
          unique_view: 0,
          unique_play: 0,
        }

        Object.values(data[device]).forEach((dateData) => {
          this.devicesInfo[device].view += dateData.view || 0
          this.devicesInfo[device].play += dateData.play || 0
          this.devicesInfo[device].unique_view += dateData.unique_view || 0
          this.devicesInfo[device].unique_play += dateData.unique_play || 0

          this.summarizedDevicesInfo[category].view += dateData.view || 0
          this.summarizedDevicesInfo[category].play += dateData.play || 0
          this.summarizedDevicesInfo[category].unique_view +=
            dateData.unique_view || 0
          this.summarizedDevicesInfo[category].unique_play +=
            dateData.unique_play || 0
        })
      })
    },
    updateChartSeries() {
      this.series = [
        this.summarizedDevicesInfo.desktop.percentage,
        this.summarizedDevicesInfo.mobile.percentage,
        this.summarizedDevicesInfo.others.percentage,
      ]
    },
    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      exportToCsv(
        `analytics-devices-${startDateFormatted}--${endDateFormatted}.csv`,
        this.currentlyVisibleColumns,
        this.tableItems
      )
    },
    async generateAndDownloadPng() {
      this.isCapturingPng = true 
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const chartsElement = this.$refs.chartsContainerForCapture
      if (!chartsElement) {
        console.error('Elemento dos gráficos não encontrado para exportação.')
        return
      }
      try {
        const lines = chartsElement.querySelectorAll('line')
        for (const line of lines) {
          line.remove()
        }
        const textTruncateFixCss = `
          * {
            text-overflow: initial !important;
            overflow: visible !important;
            white-space: nowrap !important; 
          }
          .charts-container .d-flex {
            white-space: nowrap !important;
          }
        `
        const fontCss = await getEmbeddedFontCss()
        const imgDataUri = await toPng(chartsElement, {
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
          skipFonts: false,
          preferredFontFormat: 'woff2',
          fontEmbedCSS: fontCss + textTruncateFixCss,
          style: { fontFamily: 'Rubik, sans-serif' }
        })
        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        const link = document.createElement('a')
        link.download = `devices-${startDateFormatted}--${endDateFormatted}.png`
        link.href = imgDataUri
        link.click()
      } catch (error) {
        console.error('Erro ao gerar PNG:', error)
        this.UPDATE_ERROR_STATE(true)
      } finally {
        this.isCapturingPng = false
      }
    },
    async generatePdf() {
      this.isCapturingPdf = true 
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const chartsElement = this.$refs.chartsContainerForPdf

      if (!chartsElement) {
        console.error('Elemento dos gráficos não encontrado para exportação.')
        return
      }

      try {
        const lines = chartsElement.querySelectorAll('line')
        for (const line of lines) {
          line.remove()
          
        }
        const textTruncateFixCss = `
          * {
            text-overflow: initial !important;
            overflow: visible !important;
            white-space: nowrap !important; 
          }
          .charts-container .d-flex {
            white-space: nowrap !important;
          }
        `
        const fontCss = await getEmbeddedFontCss()
        const imgDataUri = await toPng(chartsElement, {
          backgroundColor: '#FFFFFF',
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
          preferredFontFormat: 'woff2',
          fontEmbedCSS: fontCss + textTruncateFixCss,
          style: { fontFamily: 'Rubik, sans-serif' }
        })

        const img = new Image()
        img.src = imgDataUri
        await new Promise((resolve) => {
          img.onload = resolve
        })

        const pdfImageWidth = 186
        const pdfImageHeight = (img.height * pdfImageWidth) / img.width

        const headerData = {
          title: this.$t('message.pages.analyticsV3.pages.devices.title'),
          description: this.$t(
            'message.pages.analyticsV3.pages.devices.description'
          ),
          metrics: [],
        }

        const sortedDataForPdf = [...this.tableItems].sort((a, b) => {
          const playsA = a.play || 0
          const playsB = b.play || 0
          return playsB - playsA
        })

        const tableHeaders = this.visibleTableColumns.map((col) => col.text)
        const tableBody = sortedDataForPdf.map((item) =>
          this.visibleTableColumns.map((col) => {
            const value = item[col.value]
            if (col.value === 'play_rate') {
              return value
            }
            if (typeof value === 'number') {
              return value.toLocaleString()
            }
            return value || '-'
          })
        )

        const columnStyles = {
          0: { cellWidth: 'auto', overflow: 'ellipsize' },
          1: { cellWidth: 22 },
          2: { cellWidth: 22 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
        }

        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        const filename = `devices-${startDateFormatted}--${endDateFormatted}.pdf`

        const pdfBlob = await generatePdfBlob({
          chartHeaderData: headerData,
          htmlImageData: {
            uri: imgDataUri,
            width: pdfImageWidth,
            height: pdfImageHeight,
          },
          tableHeaders: tableHeaders,
          tableBody: tableBody,
          columnStyles: columnStyles,
        })

        this.$root.$emit('pdf-ready', { blob: pdfBlob, filename: filename })
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
        this.UPDATE_ERROR_STATE(true)
      } finally {
        this.isCapturingPdf = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.container--devices-analytics-v3 {
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
}

.main--devices-analytics-v3 {
  flex: 1;
  overflow: auto;
}

.card--devices-analytics-v3 {
  background: varFigma(neutral-100-white);
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.card--table-devices-analytics-v3 {
  background: varFigma(neutral-100-white);
  border-radius: 8px;
  padding: 24px;
  margin-top: 16px;
}
</style>

<style lang="scss">
.charts-container {
  margin: 24px -12px 0 -12px;

  .col {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }
}

.span-link:hover {
  text-decoration: underline !important;
}

.capture-clone {
  position: absolute;
  top: -9999px;
  left: -9999px;
  pointer-events: none;
  width: 950px;
  height: 380px;
  background: white;
}

.capture-pdf-clone {
  position: absolute;
  top: -4999px;
  left: -4999px;
  pointer-events: none;
  width: 900px;
  height: 380px;
  box-sizing: border-box;
}
</style>
